/*
 * NavbarContainer.jsx
 * Created by michaelbray on 10/3/17
 */

import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { throttle } from 'lodash';
import Cookies from 'js-cookie';

import * as Icons from '../../components/sharedComponents/icons/Icons';
import * as BrandingIcons from '../../components/sharedComponents/icons/branding/BrandingIcons';
import * as cartActions from '../../actions/navigation/cartActions';
import * as loginActions from '../../actions/navigation/loginActions';

import AccountPanel from '../../components/navigation/account/AccountPanel';
import SearchInputContainer from './SearchInputContainer';
import AlertsNotificationContainer from './AlertsNotificationContainer';
import MobileMenuWrapper from '../../components/navigation/mobileMenu/MobileMenuWrapper';

const combinedActions = Object.assign({},
    cartActions,
    loginActions
);

const propTypes = {
    cart: PropTypes.object,
    auth: PropTypes.object,
    modal: PropTypes.object,
    fetchCart: PropTypes.func,
    login: PropTypes.func,
    loggedIn: PropTypes.func,
    logout: PropTypes.func,
    clearShowLoginModal: PropTypes.func,
    updateAccountServiceStatus: PropTypes.func,
    extendTimer: PropTypes.func
};

const SEARCH_INPUT_QUERY_PARAM = 'headerTextQuery';
const HEADER_HEIGHT = window.innerWidth > 768 ? 79 : 73;
const TEST_ENV_HOST = "www.r1s-test.com";
// eslint-disable-next-line no-restricted-globals
const CURRENT_ENV = location.host;

export class NavbarContainer extends React.Component {
    constructor(props) {
        super(props);

        // Show the Banner by default
        let showExpandedBanner = true;

        // Attempt to grab the Banner Cookie
        const bannerCookie = Cookies.get('recreation_gov_preview_banner');

        // Acquire the viewport width
        const viewportWidth = window.innerWidth;

        // If the Banner Cookie exists, check if the user has collapsed it previously, and hide
        // the banner on render if so.
        // Also, hide the banner on mobile by default
        if ((bannerCookie && bannerCookie === 'hide') || viewportWidth <= 768) {
            // Collapse the banner if the user has done so previously
            showExpandedBanner = false;
        }

        this.state = {
            hideSearch: !this.isSearchTermPresent(props),
            searchInputFocused: false,
            showMobileMenu: false,
            showTestEnvBanner: false,
            showExpandedBanner,
            previewBannerHeight: 0
        };

        this.generateHeaderHeight = this.generateHeaderHeight.bind(this);
        this.generatePreviewBanner = this.generatePreviewBanner.bind(this);
        this.onSearchInputFocus = this.onSearchInputFocus.bind(this);
        this.onSearchInputBlur = this.onSearchInputBlur.bind(this);
        this.togglePreviewBanner = this.togglePreviewBanner.bind(this);
        this.toggleSearch = this.toggleSearch.bind(this);
        this.skipNavigation = this.skipNavigation.bind(this);
        this.clickedMobileMenuButton = throttle(this.clickedMobileMenuButton.bind(this), 300);
    }

    componentWillMount() {
        this.props.updateAccountServiceStatus();

        // Show the Test Environment Banner if we're on www.r1s-test.com
        if (CURRENT_ENV === TEST_ENV_HOST) {
            this.setState({
                showTestEnvBanner: !this.state.showTestEnvBanner
            });
        }
    }

    componentDidMount() {
        this.generateHeaderHeight();
    }

    componentWillReceiveProps(nextProps) {
        // This handles the case when a user is logged in and then logs out
        if (nextProps.auth.account === null && this.props.auth.account != null) {
            window.location.assign("/");
        }
    }

    componentWillUpdate(nextProps) {
        if (this.previewBanner.clientHeight !== this.state.previewBannerHeight) {
            this.generateHeaderHeight();
        }

        // There can be a delay in getting the updated prop when changing URLs.
        // This forces the height to be calculated when the prop changes
        if (nextProps.auth.previewBannerEnabled !== this.props.auth.previewBannerEnabled) {
            this.generateHeaderHeight();
        }
    }

    onSearchInputFocus() {
        this.setState({
            searchInputFocused: true
        });
    }

    onSearchInputBlur() {
        this.setState({
            searchInputFocused: false
        });
    }

    generateHeaderHeight() {
        // We need to set the height of the placeholder that enables the sticky header.
        // As we have multiple potential banners, we need to get the height of each rendered
        // banner and add it to the base height of the Navbar.
        let navHeaderHeight = HEADER_HEIGHT;

        const previewBannerHeight = this.previewBanner.clientHeight;

        navHeaderHeight += previewBannerHeight;

        if (this.state.showTestEnvBanner) {
            const testBannerHeight = this.testEnvBanner.clientHeight;

            navHeaderHeight += testBannerHeight;
            this.navHeader.style.marginTop = `${testBannerHeight}px`;
        }

        this.setState({
            previewBannerHeight
        }, () => {
            this.navHeaderPlaceholder.style.height = `${navHeaderHeight}px`;
        });
    }

    generatePreviewBanner() {
        // If the banner isn't enabled, show an empty div
        // We need to keep the ref for measurement purposes
        if (!this.props.auth.previewBannerEnabled) {
            return (
                <div
                    className="nav-preview-banner-container"
                    ref={(node) => this.previewBanner = node} />
            );
        }

        return (
            <div
                className="nav-preview-banner-container"
                ref={(node) => this.previewBanner = node}>
                <div className="rec-msg">
                    <Icons.IconInfo />
                    <div className="rec-msg-text">
                        You are viewing the BETA Version of the New Recreation.gov!
                    </div>

                    <a
                        className="nav-preview-banner-feedback-btn"
                        href="/feedback"
                        aria-label="Submit Feedback">
                            Submit Feedback
                    </a>

                    <button
                        className="nav-preview-banner-close-button"
                        onClick={this.togglePreviewBanner}
                        aria-label="Learn More">
                        {this.state.showExpandedBanner ? <Icons.IconChevronUp alt="Hide Additional Information" /> : <Icons.IconChevronDown alt="Show Additional Information" />}
                        <span className="nav-preview-banner-close-text">Learn More</span>
                    </button>
                </div>

                {this.state.showExpandedBanner && (
                    <div className="nav-preview-banner-content-holder">
                        <div className="nav-preview-banner-content">
                            <p>
                                Welcome to the BETA version of the new Recreation.gov, which is
                                a limited preview before our official launch in October 2018.
                            </p>
                            <p>
                                <strong>
                                    Please be aware that you cannot make or change any reservations in
                                    this BETA version of the site, nor can you log in to your account
                                    or create a new one.  The availability of campsites, tickets and
                                    permits reflected in the site today is only a placeholder
                                    and does not represent actual availability.  To
                                    make a real reservation today, please
                                    visit&nbsp;<a href="https://www.recreation.gov" target="_blank" rel="noopener noreferrer">Recreation.gov</a>.  If
                                    you have a reservation on the existing site, we&#39;ll bring it
                                    over to the new site at the end of the month.
                                </strong>
                            </p>
                            <p>
                                We are glad you&#39;re here and encourage you to take a look
                                around and let us know what you think.  Try out the trip planning
                                tool.  Try searching for your favorite location – how does the
                                search tool work for you?  Overall, what do you think of the new design?
                            </p>
                            <p>
                                You can submit your comments&nbsp;<a href="/feedback">here</a>.
                            </p>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    togglePreviewBanner() {
        const updateCookieValue = this.state.showExpandedBanner ? 'hide' : 'show';

        // Set a cookie to hide the banner in the future
        Cookies.set('recreation_gov_preview_banner', updateCookieValue, { expires: 730 });
        this.setState({
            showExpandedBanner: !this.state.showExpandedBanner
        }, () => {
            this.generateHeaderHeight();
        });
    }

    isSearchTermPresent(props) {
        return props.search[SEARCH_INPUT_QUERY_PARAM] && props.search[SEARCH_INPUT_QUERY_PARAM].length > 0;
    }

    toggleSearch(e) {
        e.preventDefault();

        this.setState({
            hideSearch: !this.state.hideSearch
        });
    }

    skipNavigation(e) {
        e.preventDefault();
        const pageContent = document.getElementById('page-content');
        const pageFocus = document.querySelector('#page-content h1');
        const yPos = pageContent.getBoundingClientRect().top;

        window.scrollTo(0, yPos);

        if (pageFocus) {
            pageFocus.focus();
        }
    }

    clickedMobileMenuButton() {
        this.setState({
            showMobileMenu: !this.state.showMobileMenu
        });
    }

    render() {
        // Collapse search bar initially
        let searchClass = "";
        let buttonLabel = "Hide Search Box";

        if (!this.state.hideSearch || this.state.searchInputFocused) {
            searchClass = "show-search";
            buttonLabel = "Show Search Box";
        }

        let mobileNavActiveClass = "";
        if (this.state.showMobileMenu) {
            mobileNavActiveClass = "nav-active";
        }

        let commercialAccountNotice = null;
        if (this.props.auth.account
            && this.props.auth.account.account
            && this.props.auth.account.account.is_commercial_account) {
            commercialAccountNotice = (
                <div className="commercial-account-title">
                    Commercial
                </div>
            )
        }

        let betaLogo = null;
        if (this.props.auth.previewBannerEnabled) {
            betaLogo = (
                <div className="nav-beta-logo">
                    BETA
                </div>
            );
        }

        const previewBanner = this.generatePreviewBanner();

        let testEnvBanner = null;
        if (this.state.showTestEnvBanner) {
            testEnvBanner = (
                <div
                    className="nav-test-env-banner"
                    ref={(node) => this.testEnvBanner = node}>
                    Recreation.com Test Environment
                </div>
            );
        }

        return (
            <div>
                <a
                    href="#page-content"
                    className="skip-navigation"
                    onClick={this.skipNavigation}>
                    Skip to main content
                </a>
                <div
                    className="nav-header-placeholder"
                    ref={(node) => {
                        this.navHeaderPlaceholder = node;
                    }} />
                <header
                    role="banner"
                    className="nav-header-container sticky">
                    {previewBanner}
                    {testEnvBanner}
                    <div
                        className="nav-header"
                        ref={(node) => this.navHeader = node}>
                        <div className="nav-header-left">
                            <div className="nav-logo-holder">
                                <a href="/" title="home page">
                                    <BrandingIcons.Logo />
                                </a>
                                {betaLogo}
                                {commercialAccountNotice}
                            </div>
                        </div>
                        <nav className="nav-header-right">
                            <div className="nav-interactions-holder">
                                <div className="nav-search-bar-wrap">
                                    <button
                                        className={`nav-interactions-search-icon ${searchClass}`}
                                        onClick={(e) => this.toggleSearch(e)}>
                                        <Icons.IconSearch alt={buttonLabel} />
                                    </button>
                                    <div className={`nav-search-input-wrapper ${searchClass}`}>
                                        <SearchInputContainer
                                            queryCriteriaPropertyName={SEARCH_INPUT_QUERY_PARAM}
                                            onBlur={this.onSearchInputBlur}
                                            onFocus={this.onSearchInputFocus}
                                            searchInputID="navbar-search-input" />
                                    </div>
                                </div>
                                <div className="nav-links-wrap">
                                    <AccountPanel
                                        accountServiceEnabled={this.props.auth.accountServiceEnabled}
                                        cart={this.props.cart}
                                        account={this.props.auth.account}
                                        loginError={this.props.auth.loginError}
                                        lockout={this.props.auth.lockout}
                                        mfaRequired={this.props.auth.mfaRequired}
                                        fetchCart={this.props.fetchCart}
                                        login={this.props.login}
                                        loggedIn={this.props.loggedIn}
                                        logout={this.props.logout}
                                        showLoginModal={this.props.modal.showLoginModal}
                                        clearShowLoginModal={this.props.clearShowLoginModal}
                                        extendTimer={this.props.extendTimer} />
                                </div>
                            </div>
                            <button
                                className={`nav-mobile-menu-button ${mobileNavActiveClass}`}
                                aria-label="Navigation Menu"
                                onClick={this.clickedMobileMenuButton}>
                                <Icons.IconRecMenu />
                            </button>
                        </nav>
                    </div>
                </header>
                <AlertsNotificationContainer />
                <MobileMenuWrapper
                    showMobileMenu={this.state.showMobileMenu}
                    clickedMobileMenuButton={this.clickedMobileMenuButton}
                    previewBannerHeight={this.state.previewBannerHeight}
                    logout={this.props.logout}
                    cart={this.props.cart}
                    auth={this.props.auth} />
            </div>
        );
    }
}

NavbarContainer.propTypes = propTypes;

export default connect(
    (state) => ({
        search: state.search,
        cart: state.cart,
        auth: state.auth,
        modal: state.modal
    }),
    (dispatch) => bindActionCreators(combinedActions, dispatch)
)(NavbarContainer);
